import { useEffect, useMemo, useState } from "react";
import AgendaColMainComp from "../components/AgendaColMainComp";
import {
  AppointmentsId,
  ClosedRdvCase,
  EmptyRdvCase,
  holidayZoneSchema,
  PatientFlags,
  Timed,
} from "cadendar-shared";
import { useAtomValue, useSetAtom } from "jotai";
import {
  isStrategorRunningAtom,
  RdvProposalAtom,
  selectedRdvIdAtom,
  selectedWeekAtom,
  usePreferences,
  useRdvPopupState,
} from "../../main/state/globalState";
import _ from "underts";
import { DateTime } from "luxon";
import rdvByDate from "../../rdvs/filterTransforms/rdvsByDate";
import replaceWithFake from "../../strategor/filterTransforms/replaceWithFake";
import { useRelevantHoraires } from "../../horaires/hooks/useRelevantHoraires";
import { useThreeWeeksRdvs } from "../../rdvs/queries/rdvsQueries";
import useRdvCandidats from "../../ficheAppel/hooks/useRdvCandidats";
import useRdvProposal from "../../ficheAppel/hooks/useRdvProposal";
import isCancelledRdv from "../../rdvs/filterTransforms/isCancelledRdv";
import { trpc } from "../../main/components/trpc.ts";
import { ClosedByHoraireRdv } from "cadendar-shared";
import { Transforms } from "cadendar-shared";
import useUser from "../../users/hooks/useUser.ts";
import { toast } from "react-toastify";
import StaffAbsenceDayMenu from "../components/StaffAbsenceDayMenu.tsx";

const fakeAccordingToStrategor = (
  weekRdvsToDisplay: (
    | ClosedByHoraireRdv
    | (Timed & {
        tag: "rdv";
        date: Date;
        id: AppointmentsId;
        birthdate: string | null;
        name: string | null;
        forename: string | null;
        object: string | null;
        type: number;
        patient_id: string | null;
        addressed_by: string | null;
        patient_flags: PatientFlags | null;
        confirmation_sent: boolean | null;
        confirmation_sent_at: Date | null;
        confirmation_answer_is_positive: boolean | null;
        confirmation_forced: boolean | null;
      })
    | ClosedRdvCase
    | EmptyRdvCase
  )[][],
  rdvCandidates: {
    hour: number;
    minute: number;
    date: Date;
    score: number;
  }[],
  duration: number,
  dureeMiniRdv: number,
) => {
  //on remplace toutes les cases vides par des fake
  const emptyCasesReplacedWithFake = weekRdvsToDisplay.map((dayRdvs) =>
    dayRdvs.map((rdv) => replaceWithFake(rdv)),
  );
  const candidatesWithDuree = rdvCandidates.map((c) => ({ ...c, duration }));
  const candidatesGroupedByDate = Object.values(
    _.groupBy(candidatesWithDuree, "date"),
  );
  const result = Transforms.rdvs.openCandidateCasesForWeeks(
    emptyCasesReplacedWithFake,
    candidatesGroupedByDate,
    dureeMiniRdv,
  );
  return result;
};

// const AgendaContainer = connect(
//   mapStateToProps,
//   mapDispatchToProps
// )(AgendaColMainComp);

const AgendaContainer2 = () => {
  const [now, setNow] = useState(new Date());
  useEffect(() => {
    const interval = setInterval(() => {
      setNow(new Date());
    }, 1000 * 30);
    return () => clearInterval(interval);
  }, []);
  const selectedWeek = useAtomValue(selectedWeekAtom);
  const user = useUser();
  const selectedWeekDates = _.range(0, 6, 1).map((d) =>
    DateTime.fromJSDate(selectedWeek).plus({ days: d }).toJSDate(),
  );
  const selectedWeekDateWindow = useMemo(
    () => ({
      startDate: DateTime.fromJSDate(selectedWeek).toISODate() || "",
      endDate:
        DateTime.fromJSDate(selectedWeek).plus({ days: 5 }).toISODate() || "",
    }),
    [selectedWeek],
  );
  const [openedStaffAbsenceDay, setOpenedStaffAbsenceDay] = useState<{
    day: Date;
    top: number;
    left: number;
    columnNumber: number;
  } | null>(null);
  const [relevantHoraire] = useRelevantHoraires(selectedWeek);
  const horairesExtremes = (relevantHoraire &&
    Transforms.horaires.horairesExtremesForWeek(relevantHoraire)) || {
    start: { hour: 0, minute: 0 },
    end: { hour: 23, minute: 59 },
  };
  const débutHeureAffichage =
    Transforms.horaires.getDebutHeureAffichage(horairesExtremes);
  const finHeureAffichage =
    Transforms.horaires.getFinHeureAffichage(horairesExtremes);
  const [threeWeeksRdvs] = useThreeWeeksRdvs(selectedWeek);
  const prefs = usePreferences();
  const holidayZone = holidayZoneSchema.parse(
    prefs?.holiday_zone ?? "metropole",
  );
  const holidayDateWindow = useMemo(
    () => ({
      startDate: DateTime.fromJSDate(selectedWeek).startOf("week").toJSDate(),
      endDate: DateTime.fromJSDate(selectedWeek)
        .plus({ days: 6 })
        .startOf("day")
        .toJSDate(),
    }),
    [selectedWeek],
  );
  const holidaysQuery = trpc.publicHoliday.getByDateWindow.useQuery(
    holidayDateWindow,
    {
      enabled: !!prefs && !!holidayZone,
      placeholderData: (value) => value,
      staleTime: 1000 * 60 * 60 * 24,
      refetchOnWindowFocus: false,
    },
  );
  const duréeMinRdv = prefs?.duree_min_rdv || 15;
  const isStrategorRunning = useAtomValue(isStrategorRunningAtom);
  const regularWeekToDisplay =
    (relevantHoraire &&
      selectedWeekDates.map((date) =>
        Transforms.rdvs.dayClosedReplacer(
          Transforms.rdvs.getRdvsWithEmpties(
            Transforms.horaires.bornesForDay(relevantHoraire, date),
            isStrategorRunning
              ? rdvByDate(threeWeeksRdvs, date).filter(
                  (rdv) => !isCancelledRdv(rdv),
                )
              : rdvByDate(threeWeeksRdvs, date),
            duréeMinRdv,
            débutHeureAffichage,
            finHeureAffichage,
            date,
          ),
          date,
          débutHeureAffichage,
          finHeureAffichage,
        ),
      )) ||
    [];
  const [rdvCandidates] = useRdvCandidats();
  const setRdvProposal = useSetAtom(RdvProposalAtom);
  const presentationQuery = trpc.strategor.getPresentationSettings.useQuery();
  const cadendarPresentation = presentationQuery.data?.cadendar;
  const rdvCandidatesFiltered = rdvCandidates.filter(
    (candidate) =>
      !cadendarPresentation?.filterByMinimumScore ||
      candidate.score >= cadendarPresentation.minimumScore,
  );
  //const duree = _.first(rdvCandidatesFiltered)?.duree;
  const { rdvProposal } = useRdvProposal();
  const { duration } = rdvProposal;
  const deductedDuree = duration || _.first(rdvCandidates)?.duration;

  const weekRdvsToDisplay = isStrategorRunning
    ? deductedDuree
      ? fakeAccordingToStrategor(
          regularWeekToDisplay,
          rdvCandidatesFiltered,
          deductedDuree,
          duréeMinRdv,
        )
      : regularWeekToDisplay
    : regularWeekToDisplay;

  const { onOpenRdvPopup } = useRdvPopupState();
  const trpcUtils = trpc.useUtils();
  const archiveMutation = trpc.rdv.archive.useMutation({
    onSuccess: () => {
      trpcUtils.rdv.invalidate();
    },
  });
  const onArchiveRdv = async (id: string) => {
    archiveMutation.mutate({ _id: id });
  };
  const confirmMutation = trpc.rdv.confirm.useMutation({
    onSuccess: () => {
      trpcUtils.rdv.invalidate();
    },
  });
  const onConfirmRdv = async (_id: string) => {
    console.log("confirmMutation", _id);
    confirmMutation.mutate({ _id });
  };
  const setRdvIdSelected = useSetAtom(selectedRdvIdAtom);
  const onCaseClick = (
    rdvCase:
      | { tag: "empty"; date: Date; hour: number; minute: number }
      | { tag: "rdv"; id: string },
  ) => {
    if (rdvCase.tag === "empty") {
      setRdvProposal((proposal) => ({
        ...(proposal ? proposal : null),
        date: rdvCase.date,
        hour: rdvCase.hour,
        minute: rdvCase.minute,
      }));
      onOpenRdvPopup("rdvCreate");
      return;
    }

    if (rdvCase.tag === "rdv") {
      setRdvIdSelected(rdvCase.id);
      // setOldRdvFromRdv(rdvCase);
      onOpenRdvPopup("rdvEdit");
      return;
    }
  };

  const holidayNamesByDate = Object.fromEntries(
    (holidaysQuery.data || []).map((holiday) => [holiday.date, holiday.name]),
  ) as Record<string, string>;

  const holidayNames = selectedWeekDates.map((date) => {
    const isoDate = DateTime.fromJSDate(date).toISODate();
    if (!isoDate) {
      return null;
    }
    return holidayNamesByDate[isoDate] || null;
  });

  const staffAbsenceAgendaData = trpc.staffAbsence.getAgendaData.useQuery(
    selectedWeekDateWindow,
    {
      enabled: user?.tag === "doctor",
      placeholderData: (value) => value,
    },
  );
  const staffMembers = staffAbsenceAgendaData.data?.staff || [];
  const staffAbsences = staffAbsenceAgendaData.data?.absences || [];
  const staffAbsencesByDate = staffAbsences.reduce<
    Record<string, typeof staffAbsences>
  >((acc, absence) => {
    const current = acc[absence.absenceDate] || [];
    acc[absence.absenceDate] = [...current, absence];
    return acc;
  }, {});
  const dayAbsenceSummaries = selectedWeekDates.map((date) => {
    const isoDate = DateTime.fromJSDate(date).toISODate();
    if (!isoDate) {
      return [];
    }

    return (staffAbsencesByDate[isoDate] || []).map((absence) => ({
      absenceId: absence.id,
      staffId: absence.staffId,
      staffFullname: absence.staffFullname,
      partOfDay: absence.partOfDay,
      leaveType: absence.leaveType,
    }));
  });

  const selectedDayIsoDate = openedStaffAbsenceDay
    ? DateTime.fromJSDate(openedStaffAbsenceDay.day).toISODate()
    : null;
  const selectedDayAbsences = selectedDayIsoDate
    ? staffAbsencesByDate[selectedDayIsoDate] || []
    : [];

  const upsertStaffAbsenceMutation = trpc.staffAbsence.upsert.useMutation({
    onSuccess: async () => {
      await trpcUtils.staffAbsence.getAgendaData.invalidate();
      toast.success("Absence enregistrée.");
    },
    onError: () => {
      toast.error("Impossible d'enregistrer l'absence.");
    },
  });

  const deleteStaffAbsenceMutation = trpc.staffAbsence.delete.useMutation({
    onSuccess: async () => {
      await trpcUtils.staffAbsence.getAgendaData.invalidate();
      toast.success("Absence supprimée.");
    },
    onError: () => {
      toast.error("Impossible de supprimer l'absence.");
    },
  });

  const onDayHeaderClick = ({
    event,
    columnNumber,
  }: {
    event: React.MouseEvent<HTMLDivElement>;
    columnNumber: number;
  }) => {
    const rect = event.currentTarget.getBoundingClientRect();
    const day = selectedWeekDates[columnNumber];
    if (!day) {
      return;
    }

    setOpenedStaffAbsenceDay((current) => {
      if (
        current &&
        current.columnNumber === columnNumber &&
        DateTime.fromJSDate(current.day).hasSame(
          DateTime.fromJSDate(day),
          "day",
        )
      ) {
        return null;
      }

      return {
        day,
        columnNumber,
        top: rect.bottom + window.scrollY + 8,
        left: rect.left + rect.width / 2,
      };
    });
  };

  useEffect(() => {
    const handleCloseMenu = () => {
      setOpenedStaffAbsenceDay(null);
    };

    document.addEventListener("globalClick", handleCloseMenu);
    return () => {
      document.removeEventListener("globalClick", handleCloseMenu);
    };
  }, []);

  return (
    <>
      <AgendaColMainComp
        weekRdvsArray={weekRdvsToDisplay}
        holidayNames={holidayNames}
        dayAbsenceSummaries={dayAbsenceSummaries}
        canManageStaffAbsences={user?.tag === "doctor"}
        now={now}
        débutHeureAffichage={débutHeureAffichage}
        finHeureAffichage={finHeureAffichage}
        duréeMiniRdv={
          prefs?.duree_min_rdv ||
          weekRdvsToDisplay.flat().find((rdv) => !rdv)?.duration || //quand ça n'a pas encore laadé les préférences mais qu'on a déjà des rdvs TODO: faire mieux
          15
        }
        onCloseCrossClick={onArchiveRdv}
        onConfirmClick={onConfirmRdv}
        onCaseClick={onCaseClick}
        onDayHeaderClick={onDayHeaderClick}
      />
      {openedStaffAbsenceDay && user?.tag === "doctor" ? (
        <StaffAbsenceDayMenu
          day={openedStaffAbsenceDay.day}
          top={openedStaffAbsenceDay.top}
          left={openedStaffAbsenceDay.left}
          staff={staffMembers}
          absences={selectedDayAbsences}
          isSaving={upsertStaffAbsenceMutation.isPending}
          isDeleting={deleteStaffAbsenceMutation.isPending}
          onClose={() => setOpenedStaffAbsenceDay(null)}
          onSave={(input) => {
            upsertStaffAbsenceMutation.mutate(input);
          }}
          onDelete={(absenceId) => {
            deleteStaffAbsenceMutation.mutate({ absenceId });
          }}
        />
      ) : null}
    </>
  );
};

export default AgendaContainer2;
